import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ServerNATInfo } from '@/api/type-server'
import { Button, Trans } from '@/components'
import { Loading } from '@/components/base/Loading'
import { MachineType, paymentHours, PaymentType } from '@/consts/machine'
import { BILLING_METHODS, type BillingMethod, type Duration, ItemPriceType } from '@/consts/order'
import { Machine } from '@/models/machine'
import { formatMillicentPrice, formatPrice } from '@/utils/formatter'
import { formatHwConfig } from '@/utils/node'
import { cn } from '@/utils/style'

import {
  findSubscriptionPlan,
  formatSubscriptionDuration,
  getSubscriptionPriceCent,
} from '../utils'

interface SummaryProps {
  type?: MachineType
  period?: PaymentType
  count?: number
  machine?: Machine
  nat?: ServerNATInfo
  disabled?: boolean
  loading?: boolean
  priceAvailable?: boolean
  extraStorageGB?: number
  extraCpuCores?: number
  extraRamGB?: number
  requiredExtraStorageGB?: number
  availableExtraStorageGB?: number
  storageInsufficient?: boolean
  priceMillicent?: number
  basePriceMillicent?: number
  extraCpuPriceMillicent?: number
  extraRamPriceMillicent?: number
  extraStoragePriceMillicent?: number
  onConfirm?: () => void
  onDetailToggle?: (showDetail: boolean) => void
  billingMethod?: BillingMethod
  subscriptionTotalDays?: number
  autoRenewal?: boolean
  expirationBehavior?: boolean
  duration?: Duration
}

type OrderSummaryItemProps = {
  label: string | ReactNode
  value?: any
  formatter?: (value: any) => any
  className?: string
}

export function OrderSummaryItem({
  label,
  value,
  formatter = (v) => v,
  className,
}: OrderSummaryItemProps) {
  return (
    <div className={cn('flex flex-col gap-1 min-w-0', className)}>
      <div className="t-xs text-secondary truncate">{label}</div>
      <div
        className={cn('t-sb min-w-0 break-words', {
          'text-primary': value !== undefined,
          'text-tertiary': value === undefined,
        })}
      >
        {value !== undefined ? formatter(value) : '-'}
      </div>
    </div>
  )
}

export function OrderSummary({
  type,
  period,
  machine,
  nat,
  count,
  disabled,
  loading,
  priceAvailable,
  extraStorageGB,
  extraCpuCores = 0,
  extraRamGB = 0,
  requiredExtraStorageGB = 0,
  availableExtraStorageGB = 0,
  storageInsufficient,
  priceMillicent,
  basePriceMillicent,
  extraCpuPriceMillicent,
  extraRamPriceMillicent,
  extraStoragePriceMillicent,
  onConfirm,
  onDetailToggle,
  billingMethod,
  subscriptionTotalDays,
  autoRenewal,
  expirationBehavior,
  duration,
}: SummaryProps) {
  const { t } = useTranslation()
  const [showDetail, setShowDetail] = useState(false)
  const detailRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const isSubscription = billingMethod === BILLING_METHODS.SUBSCRIPTION && duration !== undefined

  const price = useMemo(() => {
    if (!machine || !count) return 0

    if (isSubscription && duration) {
      return (
        getSubscriptionPriceCent({
          plan: findSubscriptionPlan(machine.items, duration),
          duration,
          totalDays: subscriptionTotalDays,
          count,
          priceType: ItemPriceType.Original,
        }) ?? 0
      )
    }

    if (typeof period === 'undefined') return 0
    return machine.priceObject.price * paymentHours[period] * count
  }, [count, duration, isSubscription, machine, period, subscriptionTotalDays])

  const hw = useMemo(() => (machine ? formatHwConfig(machine.unitSpec) : undefined), [machine])
  const hasExtraStorage = (!!extraStorageGB && extraStorageGB > 0) || !!storageInsufficient
  const hasAdditionalResources = extraCpuCores > 0 || extraRamGB > 0 || hasExtraStorage

  const formatHourlyMillicentPrice = (value?: number) => {
    if (value === undefined) return '-'

    return `${formatMillicentPrice(
      value,
      false,
      Math.abs(value) / 100000 > 1 ? 'Credits' : 'Credit',
    )}/h`
  }

  const formatHourlyMillicentPriceParts = (value?: number) => {
    if (value === undefined) return { amount: '-', unit: '' }

    const formatted = String(
      formatMillicentPrice(value, false, Math.abs(value) / 100000 > 1 ? 'Credits' : 'Credit'),
    )
    const [amount, ...unitParts] = formatted.split(' ')

    return {
      amount,
      unit: unitParts.length > 0 ? `${unitParts.join(' ')}/h` : '',
    }
  }

  const renderAdditionalResourcePrice = (value?: number) => {
    const { amount, unit } = formatHourlyMillicentPriceParts(value)

    return (
      <div className="t-sm flex justify-end gap-1 text-primary">
        <span>{amount}</span>
        {unit && <span className="text-tertiary">{unit}</span>}
      </div>
    )
  }

  const formatHourlyPrice = (value: number) =>
    `${formatPrice(value, false, Math.abs(value) > 1 ? 'Credits' : 'Credit')}/h`

  const formatSubscriptionPrice = (value: number) =>
    formatPrice(value, false, Math.abs(value) > 100 ? 'Credits' : 'Credit')

  const summaryLoading = !!loading && !isSubscription
  const effectivePriceAvailable = isSubscription ? true : priceAvailable
  const totalPrice = summaryLoading || isSubscription ? undefined : priceMillicent
  const basePrice =
    summaryLoading || basePriceMillicent === undefined
      ? undefined
      : basePriceMillicent * (count ?? 1)
  const additionalPriceFromItems =
    (extraCpuPriceMillicent ?? 0) +
    (extraRamPriceMillicent ?? 0) +
    (extraStoragePriceMillicent ?? 0)
  const additionalPrice = summaryLoading
    ? undefined
    : totalPrice !== undefined && basePrice !== undefined
      ? Math.max(totalPrice - basePrice, 0)
      : additionalPriceFromItems
  const totalCreditsValue = totalPrice !== undefined ? totalPrice / 100000 : price / 100

  const subscriptionBillingItems: OrderSummaryItemProps[] = [
    {
      label: <Trans i18nKey="create.summaryLabelBilling">Billing Method</Trans>,
      value: <Trans i18nKey="create.billingMethodsOptions.subscription">Subscription</Trans>,
    },
    {
      label: <Trans i18nKey="create.summaryLabelDuration">Duration</Trans>,
      value: formatSubscriptionDuration(subscriptionTotalDays, t),
    },
    ...(typeof autoRenewal === 'boolean'
      ? [
          {
            label: <Trans i18nKey="create.summaryLabelAutoRenewal">Auto Renewal</Trans>,
            value: autoRenewal ? (
              <Trans i18nKey="create.AutoRenewalSection.yes">Yes</Trans>
            ) : (
              <Trans i18nKey="create.AutoRenewalSection.no">No</Trans>
            ),
          },
        ]
      : []),
    ...(typeof expirationBehavior === 'boolean'
      ? [
          {
            label: (
              <Trans i18nKey="create.summaryLabelExpirationBehavior">Expiration Behavior</Trans>
            ),
            value: expirationBehavior ? (
              <Trans i18nKey="create.ExpirationBehaviorSection.switchToPayAsYouGo">
                Continue running with Pay-As-You-Go
              </Trans>
            ) : (
              <Trans i18nKey="create.ExpirationBehaviorSection.releaseInstance">
                Release Instance
              </Trans>
            ),
          },
        ]
      : []),
  ]

  const baseItems: OrderSummaryItemProps[] = [
    {
      label: <Trans i18nKey="create.summaryLabelType">Type</Trans>,
      value: type,
      formatter: (type) => (type === MachineType.CPU ? 'CPU' : 'GPU'),
    },
    { label: <Trans i18nKey="create.summaryLabelUnitQty">Unit Qty</Trans>, value: count },
  ]

  const specItems: OrderSummaryItemProps[] = [
    ...(type !== undefined && Machine.getMachineType(type) === MachineType.GPU
      ? [{ label: <Trans i18nKey="create.summaryLabelGpu">GPU Model</Trans>, value: hw?.gpuName }]
      : []),
    { label: <Trans i18nKey="create.summaryLabelCpu">CPU Model</Trans>, value: hw?.cpuName },
    ...(type !== undefined && Machine.getMachineType(type) === MachineType.GPU
      ? [
          {
            label: <Trans i18nKey="create.summaryLabelGpus">GPUs</Trans>,
            value: machine?.unitSpec.gpuCores,
          },
          {
            label: <Trans i18nKey="create.summaryLabelGPURAM">GPU RAM</Trans>,
            value: hw?.gpuRamGB,
            formatter: (ram: number) => `${ram} GB`,
          },
        ]
      : []),
    {
      label: <Trans i18nKey="create.summaryLabelVCpus">vCPUs</Trans>,
      value: hw?.cpuCores,
      formatter: (cores: number) => `${cores * (count ?? 1)}`,
    },
    {
      label: <Trans i18nKey="create.summaryLabelRAM">RAM</Trans>,
      value: hw?.cpuRamGB,
      formatter: (ram: number) => `${ram * (count ?? 1)} GB`,
    },
    {
      label: <Trans i18nKey="create.summaryLabelStorage">Storage</Trans>,
      value: hw?.diskGB,
      formatter: (disk: number) => `${disk * (count ?? 1)} GB`,
    },
    ...(nat
      ? [
          {
            label: <Trans i18nKey="create.summaryLabelPublicIP">Public IP</Trans>,
            value: nat?.name ?? '-',
          },
        ]
      : []),
  ]

  useEffect(() => {
    onDetailToggle?.(showDetail)
  }, [showDetail, onDetailToggle])

  const toggleDetail = () => {
    setShowDetail((prev) => !prev)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        detailRef.current &&
        !detailRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDetail(false)
        onDetailToggle?.(false)
      }
    }

    if (showDetail) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDetail])

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden bg-panel-white p-6 shadow-card-100-reverse transition-[border-radius,gap] duration-300 ease-out',
        showDetail ? 'gap-6 rounded-[32px]' : 'gap-0 rounded-b-[32px] rounded-t-none',
      )}
      data-ai-component="order-summary"
      data-ai-state={
        summaryLoading
          ? 'loading'
          : disabled
            ? 'needs-required-selection'
            : effectivePriceAvailable === false
              ? 'price-unavailable'
              : storageInsufficient
                ? 'storage-insufficient'
                : 'ready'
      }
      data-ai-value={totalCreditsValue.toFixed(3)}
    >
      <div
        ref={detailRef}
        className={cn(
          'grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out',
          showDetail ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="flex min-h-0 flex-col gap-4 overflow-hidden">
          <div className="overflow-hidden rounded-xl border border-neutral-200">
            <div className="flex items-baseline gap-4 bg-neutral-50 px-4 py-2">
              <div className="t-bold flex-1 text-primary">
                <Trans i18nKey="create.summaryLabelBasePlan">Base Plan</Trans>
              </div>
              <div
                className={cn(
                  't-bold flex min-w-[120px] justify-end text-highlight',
                  summaryLoading && 'justify-center',
                )}
              >
                {summaryLoading ? (
                  <Loading className="h-5 w-5 mx-0" />
                ) : isSubscription ? (
                  formatSubscriptionPrice(price)
                ) : basePrice !== undefined ? (
                  formatHourlyMillicentPrice(basePrice)
                ) : (
                  formatHourlyPrice(price)
                )}
              </div>
            </div>

            <div className="flex flex-col gap-4 p-4">
              <div className="grid grid-cols-4 gap-4">
                {baseItems.map((detail, index) => (
                  <OrderSummaryItem
                    key={index}
                    label={detail.label}
                    value={detail.value}
                    formatter={detail.formatter}
                  />
                ))}
              </div>

              <div className="flex items-center gap-2">
                <div className="t-xs text-tertiary">
                  <Trans i18nKey="create.summaryLabelInstanceSpecs">Instance Specs</Trans>
                </div>
                <div className="h-px flex-1 bg-neutral-200" />
              </div>

              <div className="grid grid-cols-4 gap-x-4 gap-y-4">
                {specItems.map((detail, index) => (
                  <OrderSummaryItem
                    key={index}
                    label={detail.label}
                    value={detail.value}
                    formatter={detail.formatter}
                  />
                ))}
              </div>

              {isSubscription ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="t-xs text-tertiary">
                      <Trans i18nKey="create.summaryLabelBilling">Billing Method</Trans>
                    </div>
                    <div className="h-px flex-1 bg-neutral-200" />
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    {subscriptionBillingItems.map((detail, index) => (
                      <OrderSummaryItem
                        key={index}
                        label={detail.label}
                        value={detail.value}
                        formatter={detail.formatter}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </div>

          {!isSubscription && hasAdditionalResources ? (
            <div className="overflow-hidden rounded-xl border border-neutral-200">
              <div className="flex items-baseline gap-4 bg-neutral-50 px-4 py-2">
                <div className="t-bold flex-1 text-primary">
                  <Trans i18nKey="create.summaryLabelAdditionalResources">
                    Additional Resources
                  </Trans>
                </div>
                <div className="t-bold text-highlight">
                  {formatHourlyMillicentPrice(additionalPrice)}
                </div>
              </div>

              <div className="flex flex-col px-4">
                {hasExtraStorage && (
                  <div className="grid grid-cols-4 gap-4 py-2">
                    <div className="t-sm text-secondary">
                      <Trans i18nKey="create.summaryLabelExtraStorage">Extra Storage</Trans>
                    </div>
                    <div className="t-sm col-span-2 text-primary">
                      {storageInsufficient ? (
                        <div className="flex flex-col gap-1">
                          <span>
                            <Trans
                              i18nKey="create.summaryExtraStorageRequired"
                              values={{ size: requiredExtraStorageGB }}
                            >
                              {'{{size}}'} GB required
                            </Trans>
                          </span>
                          <span>
                            <Trans
                              i18nKey="create.summaryExtraStorageMaxAvailable"
                              values={{ size: availableExtraStorageGB }}
                            >
                              Up to {'{{size}}'} GB available
                            </Trans>
                          </span>
                          <span className="text-warning">
                            <Trans i18nKey="create.summaryExtraStorageInsufficient">
                              Not enough for the selected image or snapshot
                            </Trans>
                          </span>
                        </div>
                      ) : (
                        `${extraStorageGB} GB`
                      )}
                    </div>
                    {renderAdditionalResourcePrice(
                      summaryLoading ? undefined : (extraStoragePriceMillicent ?? 0),
                    )}
                  </div>
                )}
                {extraCpuCores > 0 && (
                  <div className="grid grid-cols-4 gap-4 py-2">
                    <div className="t-sm text-secondary">
                      <Trans i18nKey="create.summaryLabelExtraVCpus">Extra vCPUs</Trans>
                    </div>
                    <div className="t-sm col-span-2 text-primary">{extraCpuCores} Cores</div>
                    {renderAdditionalResourcePrice(
                      summaryLoading ? undefined : (extraCpuPriceMillicent ?? 0),
                    )}
                  </div>
                )}
                {extraRamGB > 0 && (
                  <div className="grid grid-cols-4 gap-4 py-2">
                    <div className="t-sm text-secondary">
                      <Trans i18nKey="create.summaryLabelExtraRAM">Extra RAM</Trans>
                    </div>
                    <div className="t-sm col-span-2 text-primary">{extraRamGB} GB</div>
                    {renderAdditionalResourcePrice(
                      summaryLoading ? undefined : (extraRamPriceMillicent ?? 0),
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-6">
          <div
            className={cn(
              'h6 flex min-w-[120px] items-center gap-2 text-panel-primary',
              summaryLoading && 'justify-center',
            )}
          >
            {summaryLoading ? (
              <Loading className="h-5 w-5 mx-0" />
            ) : isSubscription ? (
              formatSubscriptionPrice(price)
            ) : totalPrice !== undefined ? (
              formatHourlyMillicentPrice(totalPrice)
            ) : (
              formatHourlyPrice(price)
            )}
          </div>
          <div className="h-5 w-px shrink-0 bg-neutral-200" />
          {isSubscription ? (
            <>
              <div className="t-sm shrink-0 text-secondary">
                {formatSubscriptionDuration(subscriptionTotalDays, t)}
              </div>
              {autoRenewal ? (
                <>
                  <div className="h-5 w-px shrink-0 bg-neutral-200" />
                  <div className="t-sm shrink-0 text-secondary">
                    <Trans i18nKey="create.summaryLabelAutoRenewal">Auto Renewal</Trans>
                  </div>
                </>
              ) : null}
              <div className="h-5 w-px shrink-0 bg-neutral-200" />
            </>
          ) : null}
          <div className="flex gap-2 items-center">
            <div className="t-sm text-secondary">
              <Trans i18nKey="create.summaryLabelQuantity">Instance Ordered</Trans>
            </div>
            <div className="h6 text-panel-primary">{machine ? 1 : '-'}</div>
          </div>
        </div>

        <div className="flex gap-2 justify-end items-center">
          <div
            ref={buttonRef}
            onClick={machine !== undefined ? toggleDetail : undefined}
            className="t-sb text-highlight px-4 py-3 cursor-pointer relative text-nowrap"
            data-ai-action={showDetail ? 'hide-order-detail' : 'show-order-detail'}
            data-ai-state={machine === undefined ? 'disabled' : 'available'}
          >
            <span className="invisible">Show Detail</span>

            <span
              className={cn('absolute inset-0 flex items-center px-4 transition-all duration-300', {
                'opacity-0 -translate-x-4': showDetail,
                'opacity-100 translate-x-0': !showDetail,
                'text-secondary': machine === undefined,
              })}
            >
              <Trans i18nKey="create.summaryShowDetail">Show Detail</Trans>
            </span>
            <span
              className={cn('absolute inset-0 flex items-center px-4 transition-all duration-300', {
                'opacity-100 translate-x-0': showDetail,
                'opacity-0 translate-x-4': !showDetail,
              })}
            >
              <Trans i18nKey="create.summaryHideDetail">Hide Detail</Trans>
            </span>
          </div>
          <div
            onMouseDown={() => {
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur()
              }
            }}
          >
            <Button
              type="secondary"
              block
              disabled={disabled || summaryLoading || effectivePriceAvailable === false}
              onClick={onConfirm}
              data-ai-action="complete-checkout"
              data-ai-state={
                disabled || summaryLoading || effectivePriceAvailable === false
                  ? 'disabled'
                  : 'enabled'
              }
              data-ai-value={totalCreditsValue.toFixed(3)}
            >
              <Trans i18nKey="create.complete">Complete Checkout</Trans>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
